import { useState } from "react";
import {
  Button as SemanticButton,
  Form as SemanticForm,
  Input as SemanticInput,
} from "semantic-ui-react";
import { toast } from "sonner";
import { XIcon } from "lucide-react";

import usePageTitle from "hooks/usePageTitle";
import useTheme from "hooks/useTheme";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Switch } from "@/components/ui/switch";

const SEMANTIC_SIZES = [
  "mini",
  "tiny",
  "small",
  "medium",
  "large",
  "big",
  "huge",
  "massive",
];

const SHADCN_VARIANTS = [
  "default",
  "secondary",
  "outline",
  "ghost",
  "destructive",
  "link",
];

const SHADCN_SIZES = ["xs", "sm", "default", "lg"];
const SHADCN_ICON_SIZES = ["icon-xs", "icon-sm", "icon", "icon-lg"];

const BADGE_VARIANTS = [
  "default",
  "secondary",
  "destructive",
  "success",
  "warning",
  "outline",
  "ghost",
];

const BADGE_SIZES = ["sm", "default", "lg"];

// A 5-digit range, the case the Table V3 footer has to survive.
const PAGINATION_PAGES = [1, "...", 9998, 9999, 10000, "...", 47208];

const SubGroup = ({ label, children }) => (
  <div className="tw:flex tw:flex-col tw:gap-2">
    <span className="tw:text-sm tw:font-medium tw:text-muted-foreground">
      {label}
    </span>
    {children}
  </div>
);

const DemoCard = ({ title, description, children }) => (
  <Card>
    <CardHeader>
      <CardTitle>{title}</CardTitle>
      {description && <CardDescription>{description}</CardDescription>}
    </CardHeader>
    <CardContent className="tw:flex tw:flex-col tw:gap-5">
      {children}
    </CardContent>
  </Card>
);

function PaginationDemo() {
  const [page, setPage] = useState(9999);

  return (
    <Pagination className="tw:mx-0 tw:w-auto">
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            aria-label="Previous page"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          />
        </PaginationItem>
        {PAGINATION_PAGES.map((pageNum, index) => (
          <PaginationItem key={`${pageNum}-${index}`}>
            {pageNum === "..." ? (
              <PaginationEllipsis />
            ) : (
              <PaginationLink
                size="default"
                className="tw:min-w-8 tw:justify-center"
                isActive={pageNum === page}
                aria-label={`Go to page ${pageNum}`}
                onClick={() => setPage(pageNum)}
              >
                {pageNum}
              </PaginationLink>
            )}
          </PaginationItem>
        ))}
        <PaginationItem>
          <PaginationNext
            aria-label="Next page"
            onClick={() => setPage(page + 1)}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

const ComponentsPage = () => {
  usePageTitle("Components");
  const { isDark, toggleTheme } = useTheme();

  return (
    <>
      <DemoCard
        title="Theme"
        description={`Current theme: ${isDark ? "dark" : "light"}.`}
      >
        <div>
          <Button variant="outline" onClick={toggleTheme}>
            Switch to {isDark ? "light" : "dark"}
          </Button>
        </div>
      </DemoCard>

      <DemoCard title="Buttons">
        <SubGroup label="Semantic UI variants">
          <div>
            <SemanticButton primary>primary</SemanticButton>
            <SemanticButton secondary>Secondary</SemanticButton>
            <SemanticButton>Default</SemanticButton>
            <SemanticButton basic>Basic</SemanticButton>
            <SemanticButton negative>Negative</SemanticButton>
            <SemanticButton positive>Positive</SemanticButton>
          </div>
        </SubGroup>
        <SubGroup label="Semantic UI sizes">
          <div>
            {SEMANTIC_SIZES.map(size => (
              <SemanticButton key={size} size={size}>
                {size}
              </SemanticButton>
            ))}
          </div>
        </SubGroup>
        <SubGroup label="Shadcn variants">
          <div className="tw:flex tw:flex-wrap tw:gap-2">
            {SHADCN_VARIANTS.map(variant => (
              <Button key={variant} variant={variant} size="lg">
                {variant}
              </Button>
            ))}
          </div>
        </SubGroup>
        <SubGroup label="Shadcn sizes">
          <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
            {SHADCN_SIZES.map(size => (
              <Button key={size} size={size}>
                {size}
              </Button>
            ))}
          </div>
        </SubGroup>
        <SubGroup label="Shadcn icon sizes">
          <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
            {SHADCN_ICON_SIZES.map(size => (
              <Button key={size} size={size} aria-label={size}>
                +
              </Button>
            ))}
          </div>
        </SubGroup>
        <SubGroup label="Shadcn states">
          <div className="tw:flex tw:flex-wrap tw:gap-2">
            <Button disabled>Disabled</Button>
            <Button aria-invalid>Invalid</Button>
          </div>
        </SubGroup>
      </DemoCard>

      <DemoCard title="Badges">
        <SubGroup label="Variants">
          <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
            {BADGE_VARIANTS.map(variant => (
              <Badge key={variant} variant={variant}>
                {variant}
              </Badge>
            ))}
          </div>
        </SubGroup>
        <SubGroup label="Sizes">
          <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
            {BADGE_SIZES.map(size => (
              <Badge key={size} variant="secondary" size={size}>
                {size}
              </Badge>
            ))}
          </div>
        </SubGroup>
        <SubGroup label="With trailing remove icon">
          <p className="tw:text-sm tw:text-muted-foreground">
            The badge drops its own right padding when a trailing{" "}
            <code>data-icon=&quot;inline-end&quot;</code> is present, so the
            icon button supplies the spacing.
          </p>
          <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
            <Badge variant="secondary">No icon</Badge>
            {["alpha", "beta", "gamma"].map(label => (
              <Badge key={label} variant="secondary">
                {label}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  className="tw:opacity-50 tw:hover:opacity-100"
                  aria-label={`Remove ${label}`}
                >
                  <XIcon data-icon="inline-end" />
                </Button>
              </Badge>
            ))}
          </div>
        </SubGroup>
        <SubGroup label="Mono value">
          <p className="tw:text-sm tw:text-muted-foreground">
            Read-only code/id values use the <code>code</code> prop, which
            applies a monospace font.
          </p>
          <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
            <Badge variant="secondary" code>
              field_id
            </Badge>
            <Badge variant="outline" code>
              sample_value
            </Badge>
          </div>
        </SubGroup>
        <SubGroup label="Truncated value">
          <p className="tw:text-sm tw:text-muted-foreground">
            Cap the width on the Badge and wrap the value in a{" "}
            <code>min-w-0 truncate</code> child so long text ellipsizes instead
            of clipping.
          </p>
          <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
            <Badge variant="outline" code className="tw:max-w-48">
              <span className="tw:min-w-0 tw:truncate">
                utm_source_very_long_value_that_overflows
              </span>
            </Badge>
          </div>
        </SubGroup>
      </DemoCard>

      <DemoCard title="Inputs">
        <SubGroup label="Semantic UI">
          <div className="tw:flex tw:flex-col tw:gap-2 tw:max-w-80">
            <SemanticInput placeholder="Default" />
            <SemanticInput disabled placeholder="Disabled" />
            <SemanticInput error placeholder="Error" />
          </div>
        </SubGroup>
        <SubGroup label="Shadcn">
          <div className="tw:flex tw:flex-col tw:gap-2 tw:max-w-80">
            <Input placeholder="Default" />
            <Input type="email" placeholder="Email" />
            <Input type="password" placeholder="Password" />
            <Input disabled placeholder="Disabled" />
            <Input aria-invalid placeholder="Invalid" />
          </div>
        </SubGroup>
      </DemoCard>

      <DemoCard title="Checkboxes">
        <SubGroup label="Semantic UI">
          <SemanticForm>
            <SemanticForm.Checkbox label="Unchecked" />
            <SemanticForm.Checkbox label="Checked" defaultChecked />
            <SemanticForm.Checkbox
              label="Indeterminate"
              indeterminate
              defaultChecked
            />
            <SemanticForm.Checkbox label="Disabled" disabled />
            <SemanticForm.Checkbox
              label="Disabled checked"
              disabled
              defaultChecked
            />
          </SemanticForm>
        </SubGroup>
        <SubGroup label="Shadcn">
          <FieldGroup className="tw:gap-3">
            <Field orientation="horizontal">
              <Checkbox id="cb-unchecked" />
              <FieldLabel htmlFor="cb-unchecked" className="tw:font-normal">
                Unchecked
              </FieldLabel>
            </Field>
            <Field orientation="horizontal">
              <Checkbox id="cb-checked" defaultChecked />
              <FieldLabel htmlFor="cb-checked" className="tw:font-normal">
                Checked
              </FieldLabel>
            </Field>
            <Field orientation="horizontal">
              <Checkbox id="cb-indeterminate" indeterminate />
              <FieldLabel htmlFor="cb-indeterminate" className="tw:font-normal">
                Indeterminate
              </FieldLabel>
            </Field>
            <Field orientation="horizontal">
              <Checkbox id="cb-disabled" disabled />
              <FieldLabel htmlFor="cb-disabled" className="tw:font-normal">
                Disabled
              </FieldLabel>
            </Field>
            <Field orientation="horizontal">
              <Checkbox id="cb-disabled-checked" disabled defaultChecked />
              <FieldLabel
                htmlFor="cb-disabled-checked"
                className="tw:font-normal"
              >
                Disabled checked
              </FieldLabel>
            </Field>
          </FieldGroup>
        </SubGroup>
      </DemoCard>

      <DemoCard title="Switches">
        <SubGroup label="Semantic UI">
          <SemanticForm>
            <SemanticForm.Checkbox toggle label="Off" />
            <SemanticForm.Checkbox toggle label="On" defaultChecked />
            <SemanticForm.Checkbox toggle label="Disabled" disabled />
            <SemanticForm.Checkbox
              toggle
              label="Disabled on"
              disabled
              defaultChecked
            />
          </SemanticForm>
        </SubGroup>
        <SubGroup label="Shadcn">
          <FieldGroup className="tw:gap-3">
            <Field orientation="horizontal">
              <Switch id="sw-off" />
              <FieldLabel htmlFor="sw-off" className="tw:font-normal">
                Off
              </FieldLabel>
            </Field>
            <Field orientation="horizontal">
              <Switch id="sw-on" defaultChecked />
              <FieldLabel htmlFor="sw-on" className="tw:font-normal">
                On
              </FieldLabel>
            </Field>
            <Field orientation="horizontal">
              <Switch id="sw-disabled" disabled />
              <FieldLabel htmlFor="sw-disabled" className="tw:font-normal">
                Disabled
              </FieldLabel>
            </Field>
            <Field orientation="horizontal">
              <Switch id="sw-disabled-on" disabled defaultChecked />
              <FieldLabel htmlFor="sw-disabled-on" className="tw:font-normal">
                Disabled on
              </FieldLabel>
            </Field>
          </FieldGroup>
        </SubGroup>
      </DemoCard>

      <DemoCard title="Toasts">
        <div className="tw:flex tw:flex-wrap tw:gap-2">
          <Button
            variant="outline"
            onClick={() => toast.info("This is an info toast")}
          >
            Info
          </Button>
          <Button
            variant="outline"
            onClick={() => toast.success("This is a success toast")}
          >
            Success
          </Button>
          <Button
            variant="outline"
            onClick={() => toast.warning("This is a warning toast")}
          >
            Warning
          </Button>
          <Button
            variant="outline"
            onClick={() => toast.error("This is an error toast")}
          >
            Error
          </Button>
          <Button
            variant="outline"
            onClick={() => toast("This is a default toast")}
          >
            Default
          </Button>
        </div>
      </DemoCard>

      <DemoCard
        title="Pagination"
        description="Numbered pages size to their content, so a 5-digit page number does not truncate. Rendered as buttons rather than anchors: pagination here is driven by table state, not by an href."
      >
        <PaginationDemo />
      </DemoCard>
    </>
  );
};

export default ComponentsPage;
